<script setup>
import { ref } from 'vue';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';

const props = defineProps({
  title: { type: String, default: '' },
  description: { type: String, default: '' },
  confirmLabel: { type: String, default: 'Confirm' },
  cancelLabel: { type: String, default: 'Cancel' },
  confirmDestructive: { type: Boolean, default: false },
  iconPath: {
    type: String,
    default: 'M12 8h.01M11 12h1v4h1M3 12a9 9 0 1 0 18.001 0A9 9 0 0 0 3 12',
  },
});

const emit = defineEmits(['confirm', 'close']);

const isVisible = ref(false);

const open = () => {
  isVisible.value = true;
};

const close = () => {
  isVisible.value = false;
  emit('close');
};

const confirm = () => {
  isVisible.value = false;
  emit('confirm');
};

defineExpose({ open, close });
</script>

<template>
  <TeleportWithDirection to="body">
    <Transition name="lc-modal-fade">
      <div v-if="isVisible" class="lc-modal-overlay" @click.self="close">
        <div class="lc-modal-dialog" role="dialog" aria-modal="true">
          <div class="lc-modal-icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              width="32"
              height="32"
            >
              <path
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                :d="iconPath"
              />
            </svg>
          </div>
          <div class="lc-modal-title">{{ title }}</div>
          <div class="lc-modal-body">
            <div class="lc-modal-description" v-html="description" />
          </div>
          <div class="lc-modal-footer">
            <div class="lc-modal-actions">
              <button
                class="lc-modal-btn lc-modal-btn--secondary"
                type="button"
                @click="close"
              >
                {{ cancelLabel }}
              </button>
              <button
                class="lc-modal-btn"
                :class="
                  confirmDestructive
                    ? 'lc-modal-btn--destructive'
                    : 'lc-modal-btn--primary'
                "
                type="button"
                @click="confirm"
              >
                {{ confirmLabel }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </TeleportWithDirection>
</template>

<style scoped>
.lc-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.4);
  overflow: auto;
}

.lc-modal-dialog {
  display: flex;
  position: relative;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: auto;
  border-radius: 8px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  background-color: #fff;
  padding: 30px 20px;
  max-width: 400px;
  width: 90%;
  color: #131317;
  font-family:
    Inter,
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    'Helvetica Neue',
    Arial,
    sans-serif;
}

.lc-modal-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  margin-bottom: 20px;
  color: #131317;
}

.lc-modal-icon svg {
  width: 50px;
  height: 50px;
}

.lc-modal-title {
  margin: 0 0 20px;
  width: 100%;
  text-align: center;
  line-height: 1.33;
  font-size: 24px;
  font-weight: 600;
}

.lc-modal-body {
  width: 100%;
  text-align: center;
  line-height: 1.33;
  font-size: 14px;
}

.lc-modal-description {
  word-break: break-word;
}

.lc-modal-footer {
  display: flex;
  justify-content: center;
  margin-top: 32px;
  width: 100%;
  gap: 8px;
}

.lc-modal-actions {
  display: flex;
  justify-content: space-between;
  width: 100%;
}

.lc-modal-btn {
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition:
    opacity 0.2s,
    border 0.2s,
    color 0.2s,
    background-color 0.2s,
    box-shadow 0.2s;
  border-width: 1px;
  border-style: solid;
  border-radius: 8px;
  cursor: pointer;
  text-align: center;
  text-decoration: none;
  line-height: 20px;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 600;
  user-select: none;
  padding: 8px 16px;
  min-width: 36px;
  height: 36px;
  flex: 0 0 auto;
  width: calc(50% - 5px);
}

.lc-modal-btn--secondary {
  border-color: #d1d5db;
  background-color: #fff;
  color: #131317;
}

.lc-modal-btn--secondary:hover {
  background-color: #f9fafb;
}

.lc-modal-btn--destructive {
  border: 0;
  border-color: #ca0005;
  background-color: #ca0005;
  color: #fff;
}

.lc-modal-btn--destructive:hover {
  background-color: #b00004;
}

.lc-modal-btn--primary {
  border: 0;
  border-color: #0059e1;
  background-color: #0059e1;
  color: #fff;
}

.lc-modal-btn--primary:hover {
  background-color: #004ec5;
}

.lc-modal-fade-enter-active {
  animation: lc-fade-in 0.15s ease forwards;
}

.lc-modal-fade-leave-active {
  animation: lc-fade-in 0.15s ease reverse;
}

@keyframes lc-fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
</style>
